import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';

import {
  EAppState,
  ETransitionMethod,
  ROUTE_TYPE,
} from '@/constants';
import {
  logger,
  hasProperty,
  camelizeKeys,
} from '@/utils';
import { EErrorEvents } from '@/api/constants';
import { checkFeatureIsEnabled } from '@/domains/checkFeatureIsEnabled';
import { EExperimentalFeatures } from '@/domains/constants';
import { useAppStore } from '@/stores/app';
import { getVatRates } from '@/contexts/accountingContext/services';
import { TReachedResponse } from '@/api/types';
import { TVatRate, TVatRateResponse } from '@/contexts/accountingContext/domain/types';
import { showErrorNotification } from '@/domains/showErrorNotification';

// oldState -> newState -> reaction
const STATE_TRANSITIONS = {
  [EAppState.initial]: {},
  [EAppState.checking]: {},
  [EAppState.unauthenticated]: {},
  [EAppState.interactionLocked]: {},
  [EAppState.authenticated]: {},
};

/** Сетит функцию, которую необходимо вызвать после перехода между состояниями */
const registerTransition = (oldState: EAppState, newState: EAppState, transition: ETransitionMethod) => {
  STATE_TRANSITIONS[oldState][newState] = transition;
};

// регистрируем все возможные переходы между состояниями
registerTransition(EAppState.initial, EAppState.checking, ETransitionMethod.onCheck);
registerTransition(EAppState.checking, EAppState.interactionLocked, ETransitionMethod.onInteractionLock);
registerTransition(EAppState.checking, EAppState.authenticated, ETransitionMethod.onSuccessfulAuthentication);
registerTransition(EAppState.checking, EAppState.unauthenticated, ETransitionMethod.onFailedAuthentication);
registerTransition(EAppState.unauthenticated, EAppState.authenticated, ETransitionMethod.onLogin);
registerTransition(EAppState.unauthenticated, EAppState.interactionLocked, ETransitionMethod.onInteractionLock);
registerTransition(EAppState.authenticated, EAppState.unauthenticated, ETransitionMethod.onLogout);
registerTransition(EAppState.authenticated, EAppState.onMaintenance, ETransitionMethod.onMaintenance);
registerTransition(EAppState.interactionLocked, EAppState.authenticated, ETransitionMethod.onInteractionUnlock);
registerTransition(EAppState.interactionLocked, EAppState.unauthenticated, ETransitionMethod.onLogout);
registerTransition(EAppState.checking, EAppState.onMaintenance, ETransitionMethod.onMaintenance);
registerTransition(EAppState.unauthenticated, EAppState.onMaintenance, ETransitionMethod.onMaintenance);

/** Вызывает функцию подготовки данных перед переходом в состояние "аутентифицирован",
  * например, запрос списка тенантов, данные о пользователе и тд. */
const prepareDataBeforeAuthentication = (accessToken: string | null, preparationMethod: () => Promise<unknown>) => {
  if (!accessToken) return Promise.reject();
  return preparationMethod();
};

/** Вызывает коллбек для перехода между состояниями */
const transitBetweenAppStates = (
  transitionStatesMethods: Record<keyof typeof ETransitionMethod, () => Promise<void> | void>,
) => (newState: EAppState, oldState: EAppState) => {
  if (STATE_TRANSITIONS[oldState]?.[newState]) {
    const transitionName: ETransitionMethod = STATE_TRANSITIONS[oldState][newState];
    logger.log('[Global App State] Transition name:', transitionName);

    if (hasProperty(transitionStatesMethods, transitionName)) {
      transitionStatesMethods[transitionName]();
    }
  }
};

export const useAppState = (
  currentLoadingDataMethod: () => Promise<void>,
  transitionStatesMethods: Record<keyof typeof ETransitionMethod, () => Promise<void> | void>,
) => {
  const appStore = useAppStore();
  const route = useRoute();

  const isVatRatesV2Available = computed(() => checkFeatureIsEnabled(EExperimentalFeatures.vatRatesV2));

  /** Меняет состояние приложения в зависимости от того
    * аутентифицирован ли пользователь в системе */
  const performAuthCheck = () => {
    const appStore = useAppStore();
    appStore.setAppState(EAppState.checking);

    return prepareDataBeforeAuthentication(appStore.accessToken, currentLoadingDataMethod)
      .then(() => appStore.setAppState(EAppState.authenticated))
      .catch((err) => {
        if (err?.errorEvent === EErrorEvents.maintenanceMode
          || err?.errorEvent === EErrorEvents.badGatewayError
          || err?.errorEvent === EErrorEvents.intervalServerError
          || err?.errorEvent === EErrorEvents.applicationError
          || err?.errorEvent === EErrorEvents.unexpectedError) return;
        appStore.setAppState(EAppState.unauthenticated);
      });
  };

  const shouldRedirectToSignUp = computed(() => {
    if (appStore.appState === EAppState.authenticated) {
      return false;
    }

    return !appStore.isIntermediateAppState && route.meta.type === ROUTE_TYPE.private;
  });

  const changeAppState = (newState: EAppState, previousState: EAppState) => {
    transitBetweenAppStates(transitionStatesMethods)(newState, previousState);
  };

  watch(() => appStore.appState, (newValue: EAppState, oldValue: EAppState) => {
    changeAppState(newValue, oldValue);
  });

  watch(() => appStore.isAuthenticatedAppState, (newValue: boolean, oldValue: boolean | undefined) => {
    if (newValue === oldValue || !newValue) return;
    if (isVatRatesV2Available.value && !appStore.availableVatRates) {
      getVatRates()
        .then((response: TReachedResponse<TVatRateResponse>) => {
          if (!response.data) return;
          const result = response.data.map((vatRate: TVatRate) => camelizeKeys(vatRate));
          appStore.setAvailableVatRates(result);
        })
        .catch(showErrorNotification);
    }
  });

  return {
    shouldRedirectToSignUp,
    performAuthCheck,
  };
};
